import type { NodeTree, useEditor } from "@craftjs/core";
import type { LDFlagKeyMap } from "launchdarkly-react-client-sdk";
import type { StaticImageData } from "next/image";
import type {
  BlockCategory,
  BlockSelection,
  Event,
  EventSeries,
  Font,
  Schedule,
  Speaker,
  Sponsor,
  Vendor,
  Viewport,
} from "src/types";

export enum ThemeKind {
  CROSBY = "crosby",
  DEFAULT = "default",
  HAZEL = "hazel",
  NOLAN = "nolan",
}

export type ThemeColorSet = {
  title: string;
  colors: string[];
};

export type Pattern =
  | "headingOne"
  | "headingTwo"
  | "headingThree"
  | "headingFour"
  | "paragraph"
  | "description"
  | "labelOne"
  | "labelTwo";

type ThemeTypographyPatternConfig = {
  fontSize: string;
  fontWeight: number;
  lineHeight: string;
  /**
   * @deprecated
   * @hidden
   */
  color?: number;
};

type ThemeTypographyConfig = {
  patterns: {
    [P in Pattern]: ThemeTypographyPatternConfig & {
      viewport?: Partial<
        {
          [V in Exclude<
            Viewport,
            "desktop"
          >]: Partial<ThemeTypographyPatternConfig>;
        }
      >;
    };
  };
};

export type Theme = {
  kind: ThemeKind;
  name: string;
  categories: string;
  typography: ThemeTypographyConfig;
  smallPreviewUrl: StaticImageData;
  previewUrl: StaticImageData;
  previewColor: string;
  enabled: boolean;
  requiresPaymentPlanUpgrade: boolean;
  colorSet: ThemeColorSet[];
  fonts: Font[];
  blocks: Record<string, any>;
  selections: Record<BlockCategory, BlockSelection[]>;
  createTemplate: ThemeTemplateFactory;
};

export type ThemeFactoryOptions = {
  advancedFeaturesEnabled: boolean;
};

export type CraftQuery = ReturnType<typeof useEditor>["query"];

export type ThemeTemplateOptions = {
  event: Event;
  speakers: Speaker[];
  schedules: Schedule[];
  sponsors: Sponsor[];
  vendors: Vendor[];
  query: CraftQuery;
  flags: LDFlagKeyMap;
  eventSeries: EventSeries[];
};

export type ThemeTemplateFactory = (
  options: ThemeTemplateOptions
) => NodeTree[];
